import { Model } from '@vuex-orm/core';
import DeliveryAPI from '@/modules/Logistics/api/controllers/DeliveryAPI';
import DeliveryItem from '@/modules/Logistics/api/models/DeliveryItem';
import {
    DeliveryCreatePayload,
    DeliveryFetchFilters,
    DeliveryFetchResult,
    DeliveryMeta,
} from '@/modules/Logistics/interfaces/LogisticsInterfaces';

/**
 * Delivery model for logistics shipments
 * Represents a delivery list with associated items and metadata
 */
export default class Delivery extends Model {
    public static entity = 'deliveries';

    public static fields() {
        return {
            id: this.attr(null),
            date: this.attr(null),
            weekday: this.attr(null),
            number: this.attr(null),
            driver: this.attr(null),
            vehicle: this.attr(null),
            isPrinted: this.boolean(false),
            departureTime: this.attr(null),
            arrivalTime: this.attr(null),
            finalKilometers: this.attr(null),
            overallComment: this.attr(''),
            createdAt: this.attr(null),
            updatedAt: this.attr(null),
            // Relationships
            items: this.hasMany(DeliveryItem, 'delivery_id'),
        };
    }

    /**
     * Fetch all deliveries from the API
     */
    public static async fetchAll(filters?: DeliveryFetchFilters): Promise<DeliveryFetchResult> {
        try {
            const response = await DeliveryAPI.getAll(filters);
            const deliveries = response?.data || [];
            const pageNumber = filters?.pageNumber ?? 1;
            const pageSize = filters?.pageSize ?? 10;
            const total = Number(response?.request?.response?.meta?.totalCount ?? (pageNumber + 1) * pageSize);

            await Delivery.insertOrUpdate({ data: deliveries });
            return {
                deliveryIds: deliveries.map((delivery: Delivery) => String(delivery.id)),
                total,
            };
        } catch (e) {
            return Promise.reject(e);
        }
    }

    /**
     * Fetch a single delivery by ID
     */
    public static async fetchById(id: string): Promise<Delivery | null> {
        try {
            const response = await DeliveryAPI.getById(id);
            const delivery = response?.data;
            if (delivery) {
                await Delivery.insertOrUpdate({ data: delivery, insertOrUpdate: ['deliveryItem'] });
            }
            return delivery;
        } catch (e) {
            return Promise.reject(e);
        }
    }

    /**
     * Create a new delivery
     */
    public static async createNew(deliveryData: DeliveryCreatePayload): Promise<Delivery> {
        try {
            const response = await DeliveryAPI.create(deliveryData);
            const delivery = response?.data;
            if (delivery) {
                await Delivery.insertOrUpdate({ data: delivery, insertOrUpdate: ['deliveryItem'] });
            }
            return delivery;
        } catch (e) {
            return Promise.reject(e);
        }
    }

    /**
     * Add an internal order to an existing delivery
     */
    public static async addNewInternalOrderToList(
        id: string,
        projectInternalOrderId: number,
    ): Promise<void> {
        try {
            await DeliveryAPI.addNewInternalOrderToList(id, projectInternalOrderId);
        } catch (e) {
            return Promise.reject(e);
        }
    }

    /**
     * Update an existing delivery
     */
    public static async updateExisting(id: string, deliveryData: Partial<DeliveryMeta>): Promise<Delivery> {
        try {
            const response = await DeliveryAPI.update(id, deliveryData);
            const delivery = response?.data;
            if (delivery) {
                await Delivery.insertOrUpdate({ data: delivery, insertOrUpdate: ['deliveryItem'] });
            }
            return delivery;
        } catch (e) {
            return Promise.reject(e);
        }
    }

    /**
     * Delete a delivery
     */
    public static async deleteExisting(id: string): Promise<void> {
        try {
            await DeliveryAPI.delete(id);
            await Delivery.delete(id);
        } catch (e) {
            return Promise.reject(e);
        }
    }

    public static async getPDF(id: string): Promise<Blob> {
        try {
            const response = await DeliveryAPI.getPdf(id);
            return response.data;
        } catch (e) {
            return Promise.reject(e);
        }
    }

    public id!: string;
    public date!: string;
    public weekday!: string;
    public number!: string;
    public driver!: string;
    public vehicle!: string;
    public isPrinted!: boolean;
    public departureTime!: string;
    public arrivalTime!: string;
    public finalKilometers!: string;
    public overallComment!: string;
    public createdAt!: string;
    public updatedAt!: string;
    public items!: DeliveryItem[];
}
